// Todo: Combine SummaryTDOs from CBSA and Redact
// export {
//   SummaryTDOTask,
//   SummaryTDOJob,
//   VeritoneFile,
//   SummaryTDO,
// } from '@cbsa-modules/universal';

import { TDOId } from '@common-modules/universal/models/Brands';
import { Tag } from '@common/state/modules/mediaDetails/models';
import type { JobStatus, TaskStatus } from 'veritone-types';
import { FolderPathItem } from '../../helper';

/**
 * Type guard for values arriving in the aliased `mapTDOs` response.
 *
 * VE-26782: aiWARE's query-cost guard injects a `warnings` array *inside*
 * `data` (alongside the `id<tdoId>` aliases) when a query exceeds the compute
 * budget with errors disabled — it is not part of the GraphQL schema, so
 * nothing in the type system catches it. Consumers that treat every value in
 * the response as a TDO end up keying that array under `undefined` and
 * rendering it as an empty grid tile. Anything without an `id` is not a TDO.
 */
export const isSummaryTDORecord = (value: unknown): value is SummaryTDO =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  !!(value as { id?: unknown }).id;

export interface SummaryTDOTask {
  readonly id: string;
  readonly targetId?: TDOId; // TODO: is this actually used?
  // readonly taskOutput?: {
  //   readonly error?: string;
  // };
  readonly status: TaskStatus;
  readonly createdDateTime: string;
  readonly startedDateTime: string | null;
  readonly completedDateTime: string | null;
  readonly engineId: string;
  readonly engine: {
    readonly id: string;
    readonly categoryId: string;
    readonly name: string;
  };
  readonly job: {
    readonly status: JobStatus;
  };
  readonly jobId: string;
}

export interface SummaryTDOJob {
  readonly targetId: TDOId;
  readonly status: TaskStatus;
  readonly createdDateTime: string;
}

export interface VeritoneFile {
  readonly audioSampleRate?: number;
  readonly duration?: number;
  readonly fileName?: string;
  readonly fileType?: string;
  readonly filename: string;
  readonly hasAudio?: boolean;
  readonly hasVideo?: boolean;
  readonly mimetype: string;
  readonly segmented?: boolean;
  readonly height?: number;
  readonly width?: number;
  readonly videoFrameRate?: number;
  readonly size?: number;
}

export interface SummaryTDO {
  readonly id: TDOId;
  readonly name: string;
  readonly status: 'downloaded' | 'recording' | 'recorded';
  readonly thumbnailUrl: string;
  readonly createdDateTime: string; // ISO
  readonly modifiedDateTime: string; // ISO
  readonly startDateTime: string; // ISO
  readonly stopDateTime: string; // ISO
  readonly isDeleting?: boolean;
  readonly details: {
    // TODO: Not all tags are redactionStatus tags
    // readonly tags?: ReadonlyArray<{
    //   redactionStatus: 'Draft' | 'Pending Review' | 'Complete';
    //   value: 'in redaction';
    //   toBeDeletedTime?: string;
    // }>;
    readonly tags?: ReadonlyArray<
      | {
          /** Well-known labels or future custom labels on the `in redaction` tag. */
          redactionStatus: string;
          /** Stable custom-status UUID; custom statuses only (VE-24546). */
          redactionStatusKey?: string;
          value: 'in redaction';
          oldFolderPath?: FolderPathItem[];
          trashRetentionDays?: string;
          /** Legacy stamp on items trashed before the trashRetentionDays rename. */
          numberOfDaysToKeep?: string;
          movedToTrashDateTime?: string;
        }
      | Tag
      | null // We have seen tags contain null - unclear how - https://veritone.sentry.io/issues/6625961324/?alert_rule_id=621906&alert_timestamp=1747993663065&alert_type=email&environment=zsfc01-usgoveast1&notification_uuid=14d337a2-cddf-4039-8c6f-688c234c82df&project=1380797&referrer=alert_email
    >;
    readonly govQARequestId?: string;
    readonly foiaXpressRequestId?: string;
    readonly casepointRequestId?: string;
    readonly nuixRequestId?: string;
    readonly exterroRequestId?: string;
    readonly veritoneFile?: VeritoneFile;
  };
  readonly assets?: {
    readonly count: number;
    readonly records: ReadonlyArray<{
      readonly assetType: string;
      readonly details: {
        readonly audioRedactions: number;
        readonly boundingPolysCount: number;
        readonly selectedPolyGroupsCount: number;
      };
    }>;
  };
  // tasks refreshed by `fetchOrganizationTDOById` service.
  readonly tasks: {
    readonly records: ReadonlyArray<SummaryTDOTask>;
  };
  // Below retrieved by `fetchOrganizationTDOById` service.
  readonly primaryAsset?: {
    readonly id: string;
    // Present for media files (video/audio/image); absent for documents,
    // whose routing falls back to details.veritoneFile.mimetype via
    // resolveFileType. See isPdfFile.
    readonly contentType?: string;
    readonly signedUri: string;
  };
  readonly streams?: ReadonlyArray<{
    readonly protocol: string;
    readonly uri: string;
  }>;
  readonly folders?: {
    readonly id: string;
    readonly name: string;
    readonly folderPath: FolderPathItem[];
  }[];
}
